/**
 * Assignment management utilities for annotation assignments.
 *
 * This module provides hooks for managing assignment selections and CRUD
 * operations. It handles the state management for assignment overrides
 * and annotations across different stain types.
 */

import { AnnotationAssignment } from 'interfaces/annotation';
import { filter, find, findIndex, isEmpty } from 'es-toolkit/compat';
import { useCallback, useState } from 'react';

import { Input } from 'interfaces/calculateFeatures';
import { uuidv4 } from './helpers';

export enum AnnotationAssignmentUsage {
  ManualCorrection = 'manual-correction',
  InclusionExclusion = 'inclusion-exclusion',
}

export interface SelectedAssignmentDetails {
  id: string;
  modelType?: string;
  annotationAssignmentUsage?: AnnotationAssignmentUsage;
  assignment: AnnotationAssignment;
  useClassesFromIgnore?: boolean;
  classesToIgnore?: string[];
  classesToUse?: string[];
}

export interface AssignmentByStain {
  [stain: string]: SelectedAssignmentDetails[];
}

export const getSelectedAssignmentDetails = (
  assignmentInput: Input,
  slideIdStainingType: string,
  annotationAssignmentsByStain: {
    [stainType: string]: AnnotationAssignment[];
  }
): SelectedAssignmentDetails => {
  return {
    id: uuidv4(),
    modelType: assignmentInput.modelType,
    assignment: find(annotationAssignmentsByStain[slideIdStainingType], {
      annotationAssignmentId: assignmentInput.assignmentId,
    }),
    useClassesFromIgnore: isEmpty(assignmentInput.classesToUse),
    classesToIgnore: assignmentInput.classesToIgnore,
    classesToUse: assignmentInput.classesToUse,
  };
};

/**
 * Hook for managing assignment selections and operations.
 *
 * This hook provides state and methods for managing assignment selections
 * across different stain types. It includes CRUD operations for assignment
 * overrides and maintains the assignment state structure.
 *
 * @returns Object containing assignment state and management methods
 */
export const useAssignmentManagement = () => {
  const [selectedAssignments, setSelectedAssignments] = useState<AssignmentByStain>({});

  /**
   * Adds a new assignment override for the specified stain.
   * The new assignment is appended to the existing assignments for that stain.
   *
   * @param stain - The stain type to add the assignment to
   * @param newAssignment - The assignment details to add
   */
  const addNewOverrideAssignment = useCallback((stain: string, newAssignment: SelectedAssignmentDetails) => {
    setSelectedAssignments((prev) => {
      const newAssignments = { ...prev };
      newAssignments[stain] = [...(newAssignments[stain] ?? []), newAssignment];
      return newAssignments;
    });
  }, []);

  /**
   * Updates an existing assignment for the specified stain.
   * Finds the assignment by ID and replaces it with the updated version.
   *
   * @param stain - The stain type containing the assignment
   * @param updatedAssignment - The updated assignment details
   */
  const updateSelectedAssignment = useCallback((stain: string, updatedAssignment: SelectedAssignmentDetails) => {
    setSelectedAssignments((prev) => {
      const newAssignments = { ...prev };
      const updatedAssignmentIndex = findIndex(newAssignments[stain], { id: updatedAssignment.id });

      if (updatedAssignmentIndex !== -1) {
        newAssignments[stain][updatedAssignmentIndex] = updatedAssignment;
      }

      return newAssignments;
    });
  }, []);

  /**
   * Removes an assignment from the specified stain.
   * Filters out the assignment with the matching ID.
   *
   * @param stain - The stain type to remove the assignment from
   * @param assignmentOverrideId - The ID of the assignment to remove
   */
  const removeSelectedAssignment = useCallback((stain: string, assignmentOverrideId: string) => {
    setSelectedAssignments((prev) => {
      const newAssignments = { ...prev };
      newAssignments[stain] = filter(newAssignments[stain], (assignment) => assignment.id !== assignmentOverrideId);
      return newAssignments;
    });
  }, []);

  /**
   * Clears all assignments for all stains.
   * Resets the assignment state to empty.
   */
  const clearAllAssignments = useCallback(() => {
    setSelectedAssignments({});
  }, []);

  /**
   * Clears assignments for a specific stain.
   * Removes all assignments for the specified stain type.
   *
   * @param stain - The stain type to clear assignments for
   */
  const clearAssignmentsForStain = useCallback((stain: string) => {
    setSelectedAssignments((prev) => {
      const newAssignments = { ...prev };
      delete newAssignments[stain];
      return newAssignments;
    });
  }, []);

  return {
    // State
    selectedAssignments,
    setSelectedAssignments,

    // CRUD operations
    addNewOverrideAssignment,
    updateSelectedAssignment,
    removeSelectedAssignment,

    // Utility operations
    clearAllAssignments,
    clearAssignmentsForStain,
  };
};
